'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { TabBar, Tab, TransformedNewsItem, Column, Row, Section, Pagination, BreadcrumbItem } from '@/lib/types/pageBuilder';
import RowRenderer from './RowRenderer';
import { MoreHorizontal } from 'lucide-react';
import TabMenus from '../component_unique/TabMenus';
import { IncludeSectionApiData } from '@/lib/pageUtils';
import { normalizeApiEndpoint } from '@/lib/apiUtils';
import AstroPagination from '@/components/component_astro_crick/pagination';
import { useTabBreadcrumbs } from '@/contexts/TabBreadcrumbContext';

// Custom hook to calculate how many tabs fit in the container
// Optimized to avoid forced reflows during initial render
function useVisibleTabCount(containerRef, tabs, tabFontSize) {
  const $ = _c(6);
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const tabWidthsRef = useRef(t0);
  const measureScheduledRef = useRef(false);
  let t1;
  let t2;
  if ($[1] !== containerRef || $[2] !== tabFontSize || $[3] !== tabs) {
    t1 = () => {
      const measureTabs = () => {
        if (!containerRef.current || tabs.length === 0) {
          return;
        }
        requestAnimationFrame(() => {
          const measureDiv = document.createElement("div");
          measureDiv.style.cssText = "position:absolute;visibility:hidden;white-space:nowrap;font-family:inherit;pointer-events:none;";
          document.body.appendChild(measureDiv);
          const spans = [];
          tabs.forEach((tab, index) => {
            const span = document.createElement("span");
            span.className = `${index === 0 ? "pl-0" : "pl-3"} pr-3 py-3 text-12-tamil-500 font-bold`;
            span.style.cssText = `display:inline-block;white-space:nowrap;${tabFontSize ? `font-size:${tabFontSize};` : ""}`;
            span.textContent = tab.label;
            measureDiv.appendChild(span);
            spans.push(span);
          });
          requestAnimationFrame(() => {
            const widths = spans.map(_temp);
            document.body.removeChild(measureDiv);
            tabWidthsRef.current = widths;
            calculateVisible();
          });
        });
      };
      const calculateVisible = () => {
        if (!containerRef.current || tabWidthsRef.current.length === 0) {
          return;
        }
        requestAnimationFrame(() => {
          if (!containerRef.current) {
            return;
          }
          const containerWidth = containerRef.current.offsetWidth;
          const containerStyle = window.getComputedStyle(containerRef.current);
          const paddingLeft = parseFloat(containerStyle.paddingLeft) || 0;
          const paddingRight = parseFloat(containerStyle.paddingRight) || 0;
          const availableWidth = containerWidth - 50 - paddingLeft - paddingRight - 10;
          let totalWidth = 0;
          let count = 0;
          for (let i = 0; i < tabWidthsRef.current.length; i++) {
            const tabWidth = tabWidthsRef.current[i];
            if (totalWidth + tabWidth <= availableWidth) {
              totalWidth = totalWidth + tabWidth;
              count++;
            } else {
              break;
            }
          }
          if (count === tabs.length) {
            setVisibleCount(tabs.length);
          } else {
            setVisibleCount(Math.max(1, count));
          }
        });
      };
      const scheduleInitialMeasure = () => {
        if (measureScheduledRef.current) {
          return;
        }
        measureScheduledRef.current = true;
        if ("requestIdleCallback" in window) {
          window.requestIdleCallback(() => {
            measureTabs();
            measureScheduledRef.current = false;
          }, {
            timeout: 1000
          });
        } else {
          setTimeout(() => {
            measureTabs();
            measureScheduledRef.current = false;
          }, 100);
        }
      };
      scheduleInitialMeasure();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(measureTabs);
      }
      let resizeTimeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          calculateVisible();
        }, 100);
      };
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        clearTimeout(resizeTimeout);
      };
    };
    t2 = [tabs, containerRef, tabFontSize];
    $[1] = containerRef;
    $[2] = tabFontSize;
    $[3] = tabs;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return visibleCount;
}
function _temp(span_0) {
  return span_0.offsetWidth;
}
export interface DynamicTabInfo {
  id: string;
  label: string;
  slug: string;
  apiEndpoint: string;
}
interface TabBarSectionProps {
  tabBar: TabBar;
  pagination?: Pagination;
  collectionsData: Map<string, TransformedNewsItem[]>;
  dynamicTabs?: DynamicTabInfo[]; // Tabs generated from API (for dynamic mode)
  includeSectionsData?: Map<string, Section>;
  includeSectionApiData?: IncludeSectionApiData;
  stickyTabs?: boolean;
  stickyTop?: string;
}

// Helper function to check if a column has data_binding
function columnHasDataBinding(column: Column): boolean {
  return !!(column.data_binding?.api_endpoint || column.data_binding?.collection_id);
}

// Helper function to check if any column in rows has data_binding
function rowsHaveDataBinding(rows: Row[]): boolean {
  for (const row of rows) {
    for (const column of row.columns) {
      if (columnHasDataBinding(column)) {
        return true;
      }
      // Check nested columns
      if (column.columns) {
        for (const nestedCol of column.columns) {
          if (columnHasDataBinding(nestedCol)) {
            return true;
          }
        }
      }
      // Check nested rows
      if (column.rows) {
        if (rowsHaveDataBinding(column.rows)) {
          return true;
        }
      }
    }
  }
  return false;
}

// Helper function to check if column needs data_binding (has components or pagination)
function columnNeedsDataBinding(column: Column): boolean {
  return column.components && column.components.length > 0 || column.pagination?.enabled === true;
}

// Helper function to inject data_binding at TOP-LEVEL ROW only
// Nested rows inherit data through parentColumnData and dataStartIndex for sequential consumption
function injectDataBinding(rows: Row[], apiEndpoint: string, collectionId: string, limit?: number, forceOverride: boolean = false): Row[] {
  return rows.map(row => {
    // Check if row already has its own data_binding
    const hasRowBinding = !!(row.data_binding?.api_endpoint || row.data_binding?.collection_id);

    // Inject data_binding at row level if row doesn't have one (or forceOverride is true)
    const rowDataBinding = !hasRowBinding || forceOverride ? {
      api_endpoint: apiEndpoint,
      collection_id: collectionId,
      limit: limit // Include limit from dynamicConfig.content_limit
    } : row.data_binding;

    // DO NOT inject data_binding into nested rows - they should inherit via parentColumnData
    // This ensures sequential data consumption works correctly across the entire tree

    return {
      ...row,
      data_binding: rowDataBinding
    };
  });
}
function findLoopCountInRows(rows: Row[]): number | undefined {
  for (const row of rows) {
    for (const column of row.columns) {
      if (column.components) {
        for (const c of column.components) {
          if (c.data_config?.mode === 'loop' && typeof c.data_config.loop_count === 'number') {
            return c.data_config.loop_count;
          }
        }
      }
      if (column.rows) {
        const nested = findLoopCountInRows(column.rows);
        if (typeof nested === 'number') return nested;
      }
      if (column.columns) {
        for (const nestedCol of column.columns) {
          if (nestedCol.components) {
            for (const c of nestedCol.components) {
              if (c.data_config?.mode === 'loop' && typeof c.data_config.loop_count === 'number') {
                return c.data_config.loop_count;
              }
            }
          }
          if (nestedCol.rows) {
            const nested = findLoopCountInRows(nestedCol.rows);
            if (typeof nested === 'number') return nested;
          }
        }
      }
    }
  }
  return undefined;
}
export default function TabBarSection({
  tabBar,
  pagination,
  collectionsData,
  dynamicTabs,
  includeSectionsData,
  includeSectionApiData,
  stickyTabs,
  stickyTop
}: TabBarSectionProps) {
  // If tabBar is hidden, don't render anything
  if (tabBar.hidden) {
    return null;
  }

  // Get current pathname for defaultTabFromSectionUrl matching
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // For dynamic mode, use dynamicTabs if available AND not empty
  const isDynamicMode = tabBar.mode === 'dynamic' && dynamicTabs && dynamicTabs.length > 0;

  // Get the first dynamic tab ID or use the default from JSON
  // Fallback to first visible tab if defaultActiveTab doesn't exist or is hidden
  // When defaultTabFromSectionUrl is true, match current URL with tab's sectionUrl
  const getDefaultTabId = () => {
    if (isDynamicMode) {
      return dynamicTabs[0]?.id;
    }

    // Filter out hidden tabs
    const visibleTabs = tabBar.tabs.filter(t => !t.hidden);

    // If defaultTabFromSectionUrl is enabled, match URL with sectionUrl
    if (tabBar.defaultTabFromSectionUrl && pathname) {
      // Find tab whose sectionUrl matches the current pathname
      const matchingTab = visibleTabs.find(t_0 => {
        if (!t_0.sectionUrl) return false;
        // Decode and normalize both paths for comparison
        // This handles Tamil/Unicode URLs that may be encoded differently
        try {
          const decodedSectionUrl = decodeURIComponent(t_0.sectionUrl).replace(/\/$/, '');
          const decodedPathname = decodeURIComponent(pathname).replace(/\/$/, '');
          return decodedSectionUrl === decodedPathname;
        } catch {
          // Fallback to direct comparison if decoding fails
          const normalizedSectionUrl = t_0.sectionUrl.replace(/\/$/, '');
          const normalizedPathname = pathname.replace(/\/$/, '');
          return normalizedSectionUrl === normalizedPathname;
        }
      });
      if (matchingTab) {
        return matchingTab.id;
      }
      // Fall through to default behavior if no match found
    }

    // Check if defaultActiveTab exists and is not hidden
    const defaultTab = visibleTabs.find(t_1 => t_1.id === tabBar.defaultActiveTab);
    return defaultTab ? defaultTab.id : visibleTabs[0]?.id;
  };

  // Get template rows from the first static tab (used as template for all dynamic tabs)
  const templateRows = tabBar.tabs[0]?.rows || [];
  const loopCountFromTemplate = useMemo(() => findLoopCountInRows(templateRows), [templateRows]);

  // Get content_limit from dynamicConfig - this should be the primary source for fetch size
  const dynamicContentLimit = tabBar.dynamicConfig?.content_limit;
  const [activeTab, setActiveTab] = useState(getDefaultTabId);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tabBarRef = useRef<HTMLDivElement>(null);

  // Get initial page from URL query params
  const getInitialPage = useCallback(() => {
    const pageParam = searchParams.get('page');
    if (pageParam) {
      const parsed = parseInt(pageParam, 10);
      if (!isNaN(parsed) && parsed >= 1) {
        return parsed;
      }
    }
    return 1;
  }, [searchParams]);
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  // Priority: pagination.pageSize > dynamicConfig.content_limit > loopCountFromTemplate > default 10
  const [pageSize, setPageSize] = useState(pagination?.pageSize || dynamicContentLimit || loopCountFromTemplate || 10);
  const [loading, setLoading] = useState(false);
  const [tabItems, setTabItems] = useState<TransformedNewsItem[]>([]);
  const [tabItemsEndpoint, setTabItemsEndpoint] = useState<string | null>(null); // Track which endpoint tabItems belongs to
  const [totalItems, setTotalItems] = useState(100);
  const [initialized, setInitialized] = useState(false); // Track if initial data load is complete

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update active tab when pathname changes (for defaultTabFromSectionUrl)
  useEffect(() => {
    if (!tabBar.defaultTabFromSectionUrl || isDynamicMode || !pathname) {
      return;
    }
    const visibleTabs_0 = tabBar.tabs.filter(t_2 => !t_2.hidden);

    // Find tab whose sectionUrl matches the current pathname
    // Decode URLs to handle Tamil/Unicode characters that may be encoded differently
    const matchingTab_0 = visibleTabs_0.find(t_3 => {
      if (!t_3.sectionUrl) return false;
      try {
        const decodedSectionUrl_0 = decodeURIComponent(t_3.sectionUrl).replace(/\/$/, '');
        const decodedPathname_0 = decodeURIComponent(pathname).replace(/\/$/, '');
        return decodedSectionUrl_0 === decodedPathname_0;
      } catch {
        // Fallback to direct comparison if decoding fails
        const normalizedSectionUrl_0 = t_3.sectionUrl.replace(/\/$/, '');
        const normalizedPathname_0 = pathname.replace(/\/$/, '');
        return normalizedSectionUrl_0 === normalizedPathname_0;
      }
    });
    if (matchingTab_0 && matchingTab_0.id !== activeTab) {
      setActiveTab(matchingTab_0.id);
    }
  }, [pathname, tabBar.defaultTabFromSectionUrl, tabBar.tabs, isDynamicMode, activeTab]);

  // For static mode, find the active tab data
  // Fallback to first visible tab if active tab not found
  const activeStaticTabData = !isDynamicMode ? tabBar.tabs.find(tab_0 => tab_0.id === activeTab && !tab_0.hidden) || tabBar.tabs.find(tab => !tab.hidden) || tabBar.tabs[0] : null;

  // For dynamic mode, find the active dynamic tab
  const activeDynamicTab = isDynamicMode ? dynamicTabs.find(tab_1 => tab_1.id === activeTab) : null;
  const activeTabEndpoint = isDynamicMode ? activeDynamicTab?.apiEndpoint || null : null;

  // Sync local tab items from server-provided collectionsData on tab changes
  useEffect(() => {
    if (!isDynamicMode || !activeTabEndpoint) {
      return;
    }
    const initial = collectionsData.get(activeTabEndpoint) || [];
    setTabItems(initial);
    setTabItemsEndpoint(activeTabEndpoint); // Track which endpoint these items belong to
    // Mark as initialized if we have server data
    if (initial.length > 0) {
      setInitialized(true);
    }
    // Reset page to 1 when switching tabs and clear URL param
    setCurrentPage(1);
    const url = new URL(window.location.href);
    if (url.searchParams.has('page')) {
      url.searchParams.delete('page');
      window.history.replaceState({}, '', url.toString());
    }
  }, [activeTabEndpoint, collectionsData, isDynamicMode]);
  useEffect(() => {
    // Priority: pagination.pageSize > dynamicConfig.content_limit > loopCountFromTemplate > default 10
    setPageSize(pagination?.pageSize || dynamicContentLimit || loopCountFromTemplate || 10);
  }, [dynamicContentLimit, loopCountFromTemplate, pagination?.pageSize]);
  const fetchTabPage = useCallback(async (page: number, size: number) => {
    if (!isDynamicMode || !activeTabEndpoint) {
      return;
    }
    setLoading(true);
    try {
      const offset = (page - 1) * size;
      const fetchUrl = `/api/paginated-collection?endpoint=${encodeURIComponent(activeTabEndpoint)}&limit=${size}&offset=${offset}`;
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error('Failed to fetch paginated data');
      const data = await response.json();
      if (data.items) {
        setTabItems(data.items);
        setTabItemsEndpoint(activeTabEndpoint); // Track which endpoint these items belong to
      }
      if (data['total-count']) {
        setTotalItems(data['total-count']);
      }
    } catch {
      // Silently handle pagination fetch errors
    } finally {
      setLoading(false);
    }
  }, [activeTabEndpoint, isDynamicMode]);

  // Fetch page on initial load / tab change - use URL page param or default to 1
  useEffect(() => {
    if (!isDynamicMode || !activeTabEndpoint) {
      // For static mode, mark as initialized immediately
      if (!isDynamicMode) {
        setInitialized(true);
      }
      return;
    }
    const initialPage = getInitialPage();
    setCurrentPage(initialPage);

    // If page is 1 and we have initial data from server, don't re-fetch
    const initialData = collectionsData.get(activeTabEndpoint) || [];
    if (initialPage === 1 && initialData.length > 0) {
      setInitialized(true); // Mark as initialized when we have server data
      return;
    }

    // Fetch if page > 1 (from URL) or no initial data
    fetchTabPage(initialPage, pageSize).then(() => {
      setInitialized(true); // Mark as initialized after fetch completes
    });
  }, [activeTabEndpoint, collectionsData, fetchTabPage, isDynamicMode, pageSize, getInitialPage]);

  // Handle browser back/forward navigation for pagination
  useEffect(() => {
    const handlePopState = () => {
      const url_0 = new URL(window.location.href);
      const pageParam_0 = url_0.searchParams.get('page');
      const page_0 = pageParam_0 ? parseInt(pageParam_0, 10) : 1;
      if (!isNaN(page_0) && page_0 >= 1 && page_0 !== currentPage) {
        setCurrentPage(page_0);
        if (isDynamicMode && activeTabEndpoint) {
          fetchTabPage(page_0, pageSize);
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentPage, isDynamicMode, activeTabEndpoint, fetchTabPage, pageSize]);
  const handlePageChange = useCallback((page_1: number) => {
    setCurrentPage(page_1);
    fetchTabPage(page_1, pageSize);

    // Update URL with page parameter
    try {
      const url_1 = new URL(window.location.href);
      if (page_1 === 1) {
        url_1.searchParams.delete('page');
      } else {
        url_1.searchParams.set('page', page_1.toString());
      }
      window.history.pushState({}, '', url_1.toString());
    } catch {
      // Failed to update URL - silently ignore
    }
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [fetchTabPage, pageSize]);

  // Process rows for rendering
  const processedRows = useMemo(() => {
    // Get content_limit from dynamicConfig
    const contentLimit = tabBar.dynamicConfig?.content_limit;

    // Static mode - use the active tab's rows directly
    if (!isDynamicMode) {
      if (!activeStaticTabData) return [];

      // If has dynamicConfig but not using dynamicTabs, inject data_binding
      if (tabBar.dynamicConfig && !rowsHaveDataBinding(activeStaticTabData.rows)) {
        const apiEndpoint = tabBar.dynamicConfig.content_api_endpoint || tabBar.dynamicConfig.api_endpoint;
        const collectionId = tabBar.dynamicConfig.content_collection_id || tabBar.dynamicConfig.collection_id;
        if (apiEndpoint) {
          return injectDataBinding(activeStaticTabData.rows, apiEndpoint, collectionId, contentLimit);
        }
      }
      return activeStaticTabData.rows;
    }

    // Dynamic mode - use template rows with injected data_binding for active tab
    if (!activeDynamicTab) return [];

    // Inject the dynamic tab's API endpoint into template rows
    return injectDataBinding(templateRows, activeDynamicTab.apiEndpoint, activeDynamicTab.slug, contentLimit, true);
  }, [isDynamicMode, activeStaticTabData, activeDynamicTab, tabBar.dynamicConfig, templateRows]);

  // Generate tab list for rendering (filter out hidden tabs)
  const tabsToRender = isDynamicMode ? dynamicTabs.map(dt => ({
    id: dt.id,
    label: dt.label,
    sectionUrl: undefined,
    sectionUrlEnabled: false,
    breadcrumbs: undefined as BreadcrumbItem[] | undefined
  })) : tabBar.tabs.filter(t_4 => !t_4.hidden) // Filter out hidden tabs
  .map(t_5 => ({
    id: t_5.id,
    label: t_5.label,
    sectionUrl: t_5.sectionUrl,
    sectionUrlEnabled: t_5.sectionUrlEnabled,
    breadcrumbs: t_5.breadcrumbs
  }));

  // Calculate how many tabs fit (pass tabFontSize so measurement uses actual font size)
  const visibleCount = useVisibleTabCount(tabBarRef, tabsToRender, tabBar.tabFontSize);

  // Split tabs into visible and overflow
  const visibleTabs_1 = tabsToRender.slice(0, visibleCount);
  const overflowTabs = tabsToRender.slice(visibleCount);
  const hasOverflow = overflowTabs.length > 0;
  useEffect(() => {
    if (!hasOverflow && showDropdown) {
      setShowDropdown(false);
    }
  }, [hasOverflow, showDropdown]);
  const router = useRouter();
  const {
    setTabBreadcrumbs,
    clearTabBreadcrumbs,
    setFirstTabSectionUrl
  } = useTabBreadcrumbs();

  // Set first tab's sectionUrl for breadcrumb fallback navigation
  const firstTabSectionUrlSet = useRef(false);
  useEffect(() => {
    if (!firstTabSectionUrlSet.current && tabBar.tabs.length > 0) {
      // Find the first visible tab with sectionUrl
      const firstTab = tabBar.tabs.find(t_6 => !t_6.hidden && t_6.sectionUrl);
      if (firstTab?.sectionUrl) {
        const absoluteUrl = firstTab.sectionUrl.startsWith('/') ? firstTab.sectionUrl : `/${firstTab.sectionUrl}`;
        setFirstTabSectionUrl(absoluteUrl);
      }
      firstTabSectionUrlSet.current = true;
    }
  }, [tabBar.tabs, setFirstTabSectionUrl]);

  // Update breadcrumbs when active tab changes (only on initial load)
  const initialBreadcrumbsSet = useRef(false);
  useEffect(() => {
    if (!isDynamicMode && !initialBreadcrumbsSet.current) {
      const currentTab = tabBar.tabs.find(t_7 => t_7.id === activeTab && !t_7.hidden);
      if (currentTab?.sectionUrlEnabled && currentTab?.breadcrumbs && currentTab.breadcrumbs.length > 0) {
        setTabBreadcrumbs(currentTab.breadcrumbs);
      }
      initialBreadcrumbsSet.current = true;
    }
  }, [activeTab, isDynamicMode, tabBar.tabs, setTabBreadcrumbs]);
  const handleTabClick = (tabId: string, sectionUrl?: string, sectionUrlEnabled?: boolean, breadcrumbs?: BreadcrumbItem[]) => {
    // Update breadcrumbs first (before state change) to prevent flicker
    if (sectionUrlEnabled && breadcrumbs && breadcrumbs.length > 0) {
      setTabBreadcrumbs(breadcrumbs);
    } else {
      clearTabBreadcrumbs();
    }
    setActiveTab(tabId);
    setShowDropdown(false);

    // Update browser URL if sectionUrlEnabled is true and sectionUrl is provided
    if (sectionUrlEnabled && sectionUrl) {
      // Ensure sectionUrl is absolute (starts with /) to prevent URL stacking
      const absoluteUrl_0 = sectionUrl.startsWith('/') ? sectionUrl : `/${sectionUrl}`;
      // Use shallow routing to update URL without full page reload
      window.history.pushState({}, '', absoluteUrl_0);

      // Track page view in Google Analytics for the new URL
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('config', 'G-3M6WRBQKHL', {
          page_path: absoluteUrl_0
        });
      }

      // Refresh Google Ad slots for new tab content
      if (typeof window !== 'undefined' && (window as any).googletag) {
        const googletag = (window as any).googletag;
        googletag.cmd = googletag.cmd || [];
        googletag.cmd.push(() => {
          // Refresh all ad slots on the page
          googletag.pubads().refresh();
        });
      }
    }
  };
  const effectiveCollectionsData = useMemo(() => {
    if (!isDynamicMode || !activeTabEndpoint) {
      return collectionsData;
    }
    const map = new Map(collectionsData);
    // Only override with tabItems if they belong to the current active tab endpoint
    // This prevents using stale data when switching tabs
    // Use normalized endpoint key to match how ColumnContentRenderer looks up data
    if (tabItemsEndpoint === activeTabEndpoint && tabItems.length > 0) {
      const normalizedEndpoint = normalizeApiEndpoint(activeTabEndpoint);
      map.set(normalizedEndpoint, tabItems);
    }
    return map;
  }, [activeTabEndpoint, collectionsData, isDynamicMode, tabItems, tabItemsEndpoint]);
  const showTabPagination = !!(isDynamicMode && pagination?.enabled && pagination.type === 'numbered' && activeTabEndpoint && totalItems > 0 // Only show pagination if there are items
  );
  const totalPages = Math.min(Math.max(1, Math.ceil(totalItems / pageSize)), 500);

  // Check if there's no data to display - only after initialization is complete
  // This prevents showing "No Data" message during initial load
  const hasNoData = initialized && isDynamicMode && activeTabEndpoint && tabItems.length === 0 && !loading;

  // Extract styling properties from tabBar
  const {
    tabTextColor = '#4D4D4D',
    tabActiveTextColor = '#215EF9',
    tabActiveBackgroundColor,
    tabInactiveBackgroundColor,
    tabsContainerBackground = '#F8F8F8',
    tabsContainerBackgroundOpacity = 1,
    tabActiveBorderColor = '#215EF9',
    tabFontSize
  } = tabBar;

  // Compute a reduced font size for mobile (reduce by 2px)
  const mobileTabFontSize = tabFontSize ? `${Math.max(10, parseFloat(tabFontSize) - 3)}px` : undefined;

  // Calculate container background with opacity
  const containerBgStyle: React.CSSProperties = {
    backgroundColor: tabsContainerBackground,
    opacity: tabsContainerBackgroundOpacity
  };

  // If we need to apply opacity only to background, use rgba
  const getContainerBgColor = () => {
    if (tabsContainerBackgroundOpacity < 1 && tabsContainerBackground) {
      // Convert hex to rgba
      const hex = tabsContainerBackground.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${tabsContainerBackgroundOpacity})`;
    }
    return tabsContainerBackground;
  };
  return <div className="w-full" ref={containerRef}>

      {/* Tab Headers */}
      <div ref={tabBarRef} className="relative px-5" style={{
      backgroundColor: getContainerBgColor(),
      ...(stickyTabs ? {
        position: 'sticky' as const,
        top: stickyTop || '50px',
        zIndex: 45
      } : {})
    }}>
        {/* Mobile: All tabs with horizontal scroll - no 3-dot button */}
        {/* <div className="lg:hidden overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="flex gap-0 min-w-max">
            {tabsToRender.map((tab, index) => {
              const isActive = activeTab === tab.id;
              const tabStyle: React.CSSProperties = {
                color: isActive ? tabActiveTextColor : tabTextColor,
                backgroundColor: isActive ? tabActiveBackgroundColor : tabInactiveBackgroundColor,
                borderBottomWidth: isActive ? '3px' : '0',
                borderBottomStyle: 'solid',
                borderBottomColor: isActive ? tabActiveBorderColor : 'transparent',
              }; */}

        {/* Mobile: All tabs with horizontal scroll - no 3-dot button */}
        <div className="lg:hidden overflow-x-auto" style={{
        WebkitOverflowScrolling: 'touch'
      }}>
          <div className="flex gap-0 min-w-max">
            {tabsToRender.map((tab_2, index) => {
            const isActive = activeTab === tab_2.id;
            const tabStyle: React.CSSProperties = {
              color: isActive ? tabActiveTextColor : tabTextColor,
              backgroundColor: isActive ? tabActiveBackgroundColor : tabInactiveBackgroundColor,
              borderBottomWidth: isActive ? '3px' : '0',
              borderBottomStyle: 'solid',
              borderBottomColor: isActive ? tabActiveBorderColor : 'transparent',
              ...(mobileTabFontSize ? {
                fontSize: mobileTabFontSize
              } : {})
            };
            return <button key={tab_2.id} onClick={() => handleTabClick(tab_2.id, tab_2.sectionUrl, tab_2.sectionUrlEnabled, tab_2.breadcrumbs)} className={`${index === 0 ? 'pl-0' : 'pl-3'} pr-3 py-3 whitespace-nowrap transition-colors flex-shrink-0 cursor-default ${isActive ? 'font-bold leading-[1.5]' : 'font-medium leading-[1.8]'}`} style={tabStyle}>
                  {tab_2.label}
                </button>;
          })}
          </div>
        </div>

        {/* Desktop: Visible tabs with 3-dot overflow menu */}
        <div className="hidden lg:flex items-center">
          <div className="flex-1 flex gap-0 min-w-0 overflow-hidden">
            {visibleTabs_1.map((tab_3, index_0) => {
            const isActive_0 = activeTab === tab_3.id;
            const tabStyle_0: React.CSSProperties = {
              color: isActive_0 ? tabActiveTextColor : tabTextColor,
              backgroundColor: isActive_0 ? tabActiveBackgroundColor : tabInactiveBackgroundColor,
              borderBottomWidth: isActive_0 ? '3px' : '0',
              borderBottomStyle: 'solid',
              borderBottomColor: isActive_0 ? tabActiveBorderColor : 'transparent',
              ...(tabFontSize ? {
                fontSize: tabFontSize
              } : {})
            };
            return <button key={tab_3.id} onClick={() => handleTabClick(tab_3.id, tab_3.sectionUrl, tab_3.sectionUrlEnabled, tab_3.breadcrumbs)} className={`${index_0 === 0 ? 'pl-0' : 'pl-3'} pr-3 py-3 whitespace-nowrap transition-colors flex-shrink-0 cursor-default ${isActive_0 ? 'font-bold leading-[1.5]' : 'font-medium leading-[1.8]'}`} style={tabStyle_0}>
                  {tab_3.label}
                </button>;
          })}
          </div>

          {/* 3-dot overflow menu button - shows only on desktop when there are overflow tabs */}
          {hasOverflow && <div className="relative flex-shrink-0" ref={dropdownRef}>
              <button onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setShowDropdown(!showDropdown);
          }} className="flex items-center justify-center w-10 h-10 hover:bg-[#DCDCDC] rounded transition-colors" style={{
            color: tabTextColor
          }} title="மேலும்" aria-label="மேலும் தாவல்கள் காட்டு" aria-haspopup="menu" aria-expanded={showDropdown} type="button">
                <MoreHorizontal className="w-5 h-5" />
              </button>

              {/* Dropdown menu showing ONLY overflow tabs */}
              {showDropdown && <div className="absolute right-0 top-full mt-1 z-50">
                  <TabMenus items={overflowTabs.map(tab_4 => ({
              label: tab_4.label,
              isActive: activeTab === tab_4.id,
              onClick: () => handleTabClick(tab_4.id, tab_4.sectionUrl, tab_4.sectionUrlEnabled, tab_4.breadcrumbs)
            }))} activeColor={tabActiveTextColor} inactiveColor={tabTextColor} />
                </div>}
            </div>}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {hasNoData ? <div className="flex flex-col items-center justify-center py-16 px-4 bg-gray-50 dark:bg-gray-900">
            <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-lg font-semibold text-gray-600 dark:text-gray-300 mb-2">
              தரவு இல்லை
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-center">
              இந்தப் பிரிவில் தற்போது எந்தச் செய்திகளும் இல்லை
            </p>
          </div> : processedRows.map((row, index_1) => <RowRenderer key={`${activeTab}-${row.id}`} row={row} collectionsData={effectiveCollectionsData} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} isLastRow={index_1 === processedRows.length - 1} />)}
      </div>

      {showTabPagination && <div>
          <AstroPagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>}
    </div>;
}